import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthModal from '../../components/AuthModal/AuthModal';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { getMediaProgressionState, TIER_COLORS } from '../../utils/mediaProgression';
import { CuratedListsService } from '../../services/curatedListsService';
import { WatchlistService } from '../../services/watchlistService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { getCuratedListCover } from '../../utils/curatedListCover';
import SaveToListModal from '../../components/MyLists/SaveToListModal';
import styles from './CuratedListDetail.module.css';

function getCuratedItemTitle(item) {
    return item.displayTitle || (item.seasonNumber != null ? `${item.title} — Season ${item.seasonNumber}` : item.title);
}

function getCuratedItemCta(item, progression) {
    if (item.mediaType !== 'tv' || item.seasonNumber == null) return progression.ctaLabel;
    if (progression.isComplete) return 'Legendary ✓';
    if (!progression.currentTier) return `Verify Season ${item.seasonNumber}`;
    if (progression.nextTier === 'silver') return `Upgrade Season ${item.seasonNumber} to Silver`;
    if (progression.nextTier === 'gold') return `Upgrade Season ${item.seasonNumber} to Gold`;
    return progression.ctaLabel;
}

function ListItem({ item, list, user, onWatchlistChanged, onAddToList }) {
    const navigate = useNavigate();
    const { gateAction, authModalProps } = useAuthGate(user);
    const progression = getMediaProgressionState({ progress: item.progression, isGuest: !user });
    const isAdded = item.watchlist?.isAdded;

    const mediaProps = {
        list_id: list.id,
        list_slug: list.slug,
        list_type: list.listType,
        media_id: item.mediaId,
        media_type: item.mediaType,
        season_number: item.seasonNumber ?? null,
        position: item.position,
    };

    const openMedia = () => {
        trackEvent(ANALYTICS_EVENTS.CURATED_LIST_MEDIA_CLICKED, mediaProps);
        navigate(item.mediaType === 'tv' && item.seasonNumber != null
            ? `/movie/tv/${item.mediaId}/season/${item.seasonNumber}`
            : `/movie/${item.mediaType}/${item.mediaId}`);
    };

    const addToWatchlist = gateAction(async () => {
        if (isAdded) return;
        await WatchlistService.addToWatchlist(item.mediaId, item.mediaType);
        trackEvent(ANALYTICS_EVENTS.CURATED_LIST_WATCHLIST_ADDED, mediaProps);
        onWatchlistChanged(item.id);
    }, AUTH_MESSAGES.WATCHLIST);

    const verify = gateAction(() => {
        trackEvent(ANALYTICS_EVENTS.CURATED_LIST_VERIFY_CLICKED, {
            ...mediaProps,
            current_coin_tier: progression.currentTier ?? 'none',
            next_coin_tier: progression.nextTier ?? 'bronze',
        });
        navigate(item.mediaType === 'tv' && item.seasonNumber != null
            ? `/movie/tv/${item.mediaId}/season/${item.seasonNumber}`
            : `/movie/${item.mediaType}/${item.mediaId}`);
    }, AUTH_MESSAGES.QUIZ);
    const title = getCuratedItemTitle(item);
    const ctaLabel = getCuratedItemCta(item, progression);

    // Save an individual Aluvo Pick into one of the user's own My Lists.
    const addToList = gateAction(() => onAddToList(item), AUTH_MESSAGES.MY_LISTS);

    return (
        <article className={styles.item}>
            <AuthModal {...authModalProps} />
            <button className={styles.posterButton} onClick={openMedia} aria-label={`View ${title}`}>
                {item.posterUrl ? <img src={item.posterUrl} alt="" loading="lazy" /> : <span>No poster</span>}
            </button>
            <div className={styles.itemBody}>
                <div className={styles.itemTopline}>
                    <span className={styles.position}>{item.position}</span>
                    <span className={styles.kind}>{item.mediaType === 'tv' ? 'TV' : 'Movie'}</span>
                    {item.mediaType === 'tv' && item.seasonNumber != null && <span>Season {item.seasonNumber}</span>}
                    {item.year && <span>{item.year}</span>}
                </div>
                <h2>{title}</h2>
                {item.seasonName && item.seasonName !== `Season ${item.seasonNumber}` && (
                    <p className={styles.seasonName}>Season {item.seasonNumber} · {item.seasonName}</p>
                )}
                {item.editorialNote && <p className={styles.note}>{item.editorialNote}</p>}
                <div className={styles.itemActions}>
                    {progression.badgeLabel && (
                        <span
                            className={styles.tier}
                            style={{ borderColor: TIER_COLORS[progression.currentTier], color: TIER_COLORS[progression.currentTier] }}
                        >
                            {progression.badgeLabel}
                        </span>
                    )}
                    <button className={styles.primaryBtn} onClick={verify}>{ctaLabel}</button>
                    <button className={styles.secondaryBtn} onClick={addToWatchlist} disabled={isAdded}>
                        {isAdded ? 'Added ✓' : 'Add to Watchlist'}
                    </button>
                    <button className={styles.secondaryBtn} onClick={addToList}>Add to List</button>
                </div>
            </div>
        </article>
    );
}

export default function CuratedListDetail() {
    const { slug } = useParams();
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [list, setList] = useState(null);
    const [saveToListTarget, setSaveToListTarget] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [posterOpen, setPosterOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        CuratedListsService.get(slug)
            .then((data) => {
                if (cancelled) return;
                const curatedList = data.curatedList;
                setList(curatedList);
                if (curatedList) {
                    document.title = `${curatedList.title} | Aluvo`;
                    trackEvent(ANALYTICS_EVENTS.CURATED_LIST_VIEWED, {
                        list_id: curatedList.id,
                        list_slug: curatedList.slug,
                        list_type: curatedList.listType,
                        category: curatedList.category,
                        source: 'web_detail',
                        platform: 'web',
                    });
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || 'Curated list not found.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [slug]);

    useEffect(() => {
        if (!posterOpen) return undefined;
        const onKeyDown = (event) => { if (event.key === 'Escape') setPosterOpen(false); };
        window.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };
    }, [posterOpen]);

    const eligibleCount = useMemo(() => {
        if (!list?.items) return 0;
        return new Set(list.items
            .filter((item) => item.localMediaId && !item.watchlist?.isAdded && !item.progression?.hasCoin)
            .map((item) => item.localMediaId)).size;
    }, [list]);

    const markAdded = (itemId) => {
        setList((prev) => ({
            ...prev,
            items: prev.items.map((item) => {
                const addedItem = prev.items.find((candidate) => candidate.id === itemId);
                return item.id === itemId || (addedItem?.localMediaId && item.localMediaId === addedItem.localMediaId)
                    ? { ...item, watchlist: { ...(item.watchlist || {}), isAdded: true } }
                    : item;
            }),
        }));
    };

    const bulkAdd = gateAction(async () => {
        const result = await CuratedListsService.addUnwatchedToWatchlist(slug);
        trackEvent(ANALYTICS_EVENTS.CURATED_LIST_BULK_WATCHLIST_ADDED, {
            list_id: list.id,
            list_slug: list.slug,
            count: result.addedCount,
        });
        const addedMediaIds = new Set((result.items || []).map((item) => item.localMediaId).filter(Boolean));
        setSuccess(`Added ${result.addedCount} title${result.addedCount === 1 ? '' : 's'} to Watchlist.`);
        setList((prev) => ({
            ...prev,
            items: prev.items.map((item) => (
                addedMediaIds.has(item.localMediaId)
                    ? { ...item, watchlist: { ...(item.watchlist || {}), isAdded: true } }
                    : item
            )),
        }));
    }, AUTH_MESSAGES.WATCHLIST);

    const shareList = async () => {
        const url = window.location.href;
        const text = `${list.title} on Aluvo\n${list.description}\n${url}`;
        if (navigator.share) await navigator.share({ title: `${list.title} on Aluvo`, text, url });
        else await navigator.clipboard.writeText(text);
        trackEvent(ANALYTICS_EVENTS.CURATED_LIST_SHARED, { list_id: list.id, list_slug: list.slug });
        setSuccess(navigator.share ? 'Shared.' : 'Share link copied.');
    };

    if (loading) return <main className={styles.page}><div className={styles.state}>Loading…</div></main>;
    if (error || !list) return <main className={styles.page}><div className={styles.state}>{error || 'Curated list not found.'}</div></main>;

    const progress = list.progress;
    const cover = getCuratedListCover(list);

    return (
        <main className={styles.page}>
            <AuthModal {...authModalProps} />
            <SaveToListModal
                isOpen={!!saveToListTarget}
                target={saveToListTarget}
                source="curated_list_detail"
                onClose={() => setSaveToListTarget(null)}
                onSaved={(membership, { watchlistChanged, inWatchlist }) => {
                    if (watchlistChanged && inWatchlist) {
                        const added = list.items.find((item) => item.mediaId === membership?.target?.mediaId && item.mediaType === membership?.target?.mediaType);
                        if (added) markAdded(added.id);
                    }
                }}
            />
            <section className={styles.hero}>
                {cover.url ? (
                    <img src={cover.url} alt={cover.alt} className={styles.heroImage} />
                ) : (
                    <div className={`${styles.heroImage} ${styles.heroFallback}`}>Aluvo</div>
                )}
                <div className={styles.heroShade} />
                <div className={styles.heroInner}>
                    <div className={styles.heroContent}>
                        <Link to="/discover" className={styles.backLink}>← Discover</Link>
                        <span className={styles.type}>{list.listType?.replaceAll('_', ' ')}</span>
                        <h1>{list.title}</h1>
                        <p>{list.description}</p>
                        {list.longDescription && <p className={styles.long}>{list.longDescription}</p>}
                        <div className={styles.heroActions}>
                            {progress && (
                                <div className={styles.progressBox}>
                                    <strong>{progress.verified} of {progress.total} verified</strong>
                                    <span>{progress.isComplete ? 'List complete ✓' : 'List progress'}</span>
                                    <div><i style={{ width: `${progress.total ? (progress.verified / progress.total) * 100 : 0}%` }} /></div>
                                </div>
                            )}
                            {eligibleCount > 0 && (
                                <button className={styles.primaryBtn} onClick={bulkAdd}>
                                    Add {eligibleCount} unwatched title{eligibleCount === 1 ? '' : 's'} to Watchlist
                                </button>
                            )}
                            <button className={styles.secondaryBtn} onClick={shareList}>Share</button>
                        </div>
                        {success && <div className={styles.success}>{success}</div>}
                    </div>
                    <div className={styles.heroPosterWrap} aria-hidden={!cover.url}>
                        {cover.url ? (
                            <button
                                type="button"
                                className={styles.heroPosterButton}
                                onClick={() => setPosterOpen(true)}
                                aria-label={`View full-size ${list.title} cover`}
                            >
                                <img src={cover.url} alt="" className={styles.heroPoster} />
                            </button>
                        ) : (
                            <div className={`${styles.heroPoster} ${styles.heroPosterFallback}`}>Aluvo</div>
                        )}
                    </div>
                </div>
            </section>
            <section className={styles.listSection}>
                {list.items.map((item) => (
                    <ListItem
                        key={item.id}
                        item={item}
                        list={list}
                        user={user}
                        onWatchlistChanged={markAdded}
                        onAddToList={(picked) => setSaveToListTarget({ mediaId: picked.mediaId, mediaType: picked.mediaType, seasonNumber: picked.seasonNumber ?? null, title: picked.title })}
                    />
                ))}
            </section>
            {posterOpen && cover.url && (
                <div
                    className={styles.lightbox}
                    role="dialog"
                    aria-modal="true"
                    aria-label={cover.alt}
                    onClick={() => setPosterOpen(false)}
                >
                    <button
                        type="button"
                        className={styles.lightboxClose}
                        onClick={() => setPosterOpen(false)}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                    <img
                        src={cover.url}
                        alt={cover.alt}
                        className={styles.lightboxImage}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
        </main>
    );
}
